import { FC } from 'react'
import { Card, Tabs, TabsContent, TabsList, TabsTrigger, Input } from '@/components/ui'
import { ColumnSelector } from '../controls/column-selector/column-selector'
import { MultiColumnSelector } from '../controls/multi-column-selector/multi-column-selector'
import { SelectControl } from '../controls/select-control/select-control'
import { ControlType } from '../../../lib/calculators/calculator-config'
import type { CalculatorControl } from '../../../lib/calculators/calculator-config'
import type { TableDataJSON } from '../../../src/types/table-json'
import { Database, Paintbrush, HelpCircle, Info } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui'

interface ConfigPanelProps {
  controls: CalculatorControl[]
  values: Record<string, any>
  onChange: (id: string, value: any) => void
  data?: TableDataJSON | null
  tabs: Array<{
    id: string
    label: string
    controls: string[]
  }>
}

export function ConfigPanel({ controls, values, onChange, data, tabs }: ConfigPanelProps) {
  const renderControl = (control: CalculatorControl) => {
    switch (control.type) {
      case ControlType.SingleColumnSelect:
        return (
          <ColumnSelector
            key={control.id}
            label={control.label}
            value={values[control.id]}
            onChange={(value: string) => onChange(control.id, value)}
            required={control.required}
            numericOnly={control.numericOnly}
            columns={data?.columns || []}
          />
        )
      case ControlType.MultiColumnSelect:
        return (
          <MultiColumnSelector
            key={control.id}
            label={control.label}
            value={values[control.id] || []}
            onChange={(value: string[]) => onChange(control.id, value)}
            required={control.required}
            minSelections={control.minSelections}
            maxSelections={control.maxSelections}
            numericOnly={control.numericOnly}
            columns={data?.columns || []}
          />
        )
      case ControlType.Select:
        return (
          <SelectControl
            key={control.id}
            label={control.label}
            value={values[control.id]}
            onChange={(value: string) => onChange(control.id, value)}
            required={control.required}
            options={control.options}
          />
        )
      case ControlType.Number:
        return (
          <div key={control.id} className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium leading-none">
                {control.label}
              </label>
              {control.required && (
                <span className="text-red-500 text-xs leading-none">*</span>
              )}
            </div>
            <Input
              type="number"
              value={values[control.id] || ''}
              onChange={(e) => onChange(control.id, Number(e.target.value))}
              min={control.min}
              max={control.max}
              step={control.step}
              placeholder={`Enter ${control.label.toLowerCase()}...`}
              className="focus:border-primary focus:ring-1 focus:ring-primary transition-all"
            />
          </div>
        )
      case ControlType.Text:
        return (
          <div key={control.id} className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium leading-none">
                {control.label}
              </label>
              {control.required && (
                <span className="text-red-500 text-xs leading-none">*</span>
              )}
            </div>
            <Input
              type="text"
              value={values[control.id] || ''}
              onChange={(e) => onChange(control.id, e.target.value)}
              placeholder={`Enter ${control.label.toLowerCase()}...`}
              className="focus:border-primary focus:ring-1 focus:ring-primary transition-all h-9"
            />
            {values[control.id] && (
              <div className="text-xs text-muted-foreground mt-1">
                Current: {values[control.id]}
              </div>
            )}
          </div>
        )
      default:
        return null
    }
  }

  // Helper function to get the appropriate icon for a tab
  const getTabIcon = (tabLabel: string | undefined) => {
    const label = tabLabel?.toLowerCase() || '';
    if (label === 'data') return <Database className="mr-2 h-4 w-4" />;
    if (label === 'appearance') return <Paintbrush className="mr-2 h-4 w-4" />;
    return <HelpCircle className="mr-2 h-4 w-4" />;
  };

  // Helper function to get tooltip content for controls
  const getControlTooltip = (control: CalculatorControl) => {
    switch (control.type) {
      case ControlType.SingleColumnSelect:
        return 'Select a single column from your data';
      case ControlType.MultiColumnSelect:
        return 'Select multiple columns from your data';
      case ControlType.Select:
        return 'Choose from predefined options';
      case ControlType.Number:
        return `Enter a numeric value${control.min !== undefined ? ` (min: ${control.min})` : ''}${control.max !== undefined ? ` (max: ${control.max})` : ''}`;
      case ControlType.Text:
        return 'Enter text for customization';
      default:
        return '';
    }
  };

  // Helper function to render a control with a tooltip
  const renderControlWithTooltip = (control: CalculatorControl) => {
    const tooltipContent = getControlTooltip(control);
    const controlElement = renderControl(control);
    
    if (!tooltipContent) return controlElement;
    
    return (
      <div key={control.id} className="relative group">
        {controlElement}
        <div className="absolute top-0 right-0">
          <Tooltip>
            <TooltipTrigger asChild>
              <button 
                type="button" 
                className="h-5 w-5 rounded-full inline-flex items-center justify-center text-muted-foreground hover:text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                aria-label="More information"
              >
                <Info className="h-3.5 w-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right" className="max-w-xs">
              {tooltipContent}
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
    );
  };

  return (
    <Card className="h-full flex flex-col rounded-lg shadow-md border-muted/50 bg-gradient-to-b from-background via-background to-muted/20">
      <TooltipProvider>
        {tabs.length === 1 ? (
          <>
            <div className="border-b p-3 font-medium flex items-center bg-muted/30 rounded-t-lg">
              {getTabIcon(tabs[0]?.label)}
              {tabs[0]?.label}
            </div>
            <div className="flex-1 p-4 space-y-4 overflow-auto">
              {controls
                .filter(control => tabs[0]?.controls.includes(control.id))
                .map(control => renderControlWithTooltip(control))
              }
            </div>
          </>
        ) : (
          <Tabs defaultValue={tabs[0]?.id} className="flex-1 flex flex-col">
            <TabsList className="w-full flex border-b rounded-t-lg rounded-b-none bg-muted/30 p-0">
              {tabs.map(tab => (
                <TabsTrigger 
                  key={tab.id} 
                  value={tab.id} 
                  className="flex-1 flex items-center justify-center gap-2 rounded-none first:rounded-tl-lg last:rounded-tr-lg py-3 data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:shadow-none data-[state=active]:bg-background transition-all"
                >
                  {getTabIcon(tab.label)}
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>
            {tabs.map(tab => (
              <TabsContent 
                key={tab.id} 
                value={tab.id} 
                className="flex-1 p-5 space-y-5 overflow-auto animate-in fade-in-50 data-[state=active]:duration-300"
              >
                {controls
                  .filter(control => tab.controls.includes(control.id))
                  .map(control => renderControlWithTooltip(control))
                }
              </TabsContent>
            ))}
          </Tabs>
        )}
      </TooltipProvider>
    </Card>
  )
} 